/**
 * The pet parent's bearer token for standalone pet-web.
 *
 * ⛔ **Without this the standalone pages are unauthenticated and every `petParentProcedure` call
 * answers UNAUTHORIZED.** Federated components get their token from the host — care-web holds a
 * pet portal session and passes `getAuthToken` into `createAstroPetTRPCProvider`. Standalone
 * pet-web has no session and no login: a parent arrives from a link in an SMS or email and that
 * link is the whole credential.
 *
 * ⛔ **The token arrives as `?token=…` because `/t/:slug` puts it there.**
 * `apps/api/src/routes/trackedLinkRedirect.ts` appends it to the destination before its 302, using
 * a JWT minted at dispatch by `generateToken` in `packages/workflow/src/tinyUrl.ts`. That JWT
 * carries only `clinicPetParentId`, so it is namespace-blind by construction — a `Yak` run's
 * parent authenticates through exactly this path, against the same `JWT_SECRET`. The namespace
 * only ever decides which Flow database the parent's rows are read from, never who they are.
 *
 * ⚠️ **An expired tracked link redirects WITHOUT the token rather than dying.** That is deliberate
 * upstream ("the link degrades to logged-out, never dead"), so a missing token here is an ordinary
 * state, not a bug. It surfaces as the API's own UNAUTHORIZED, which the page renders as a
 * try-the-link-again message.
 */

const TOKEN_QUERY_PARAM = 'token';
const TOKEN_STORAGE_KEY = 'astroPetParentToken';

/** What `capturePetParentToken` should do with storage, given the URL it was loaded with. */
export type TokenAction =
  | { type: 'store'; token: string; scope: string }
  /** Keep what is stored — this is a reload of the page the token was captured on. */
  | { type: 'keep' }
  /** Drop what is stored — a different link, so the previous identity must not carry over. */
  | { type: 'clear' };

/**
 * The whole decision, as a pure function so it can be tested — pet-web has no DOM test
 * environment, and this is the one piece of security logic in the app worth pinning.
 *
 * ⛔ **`clear` is what stops a stale identity being reused, and the scope check is what stops
 * `clear` breaking reloads.** Both halves are load-bearing:
 *
 * - Without `clear`, a tokenless or expired link opened in a tab that already used a valid one
 *   authenticates as the PREVIOUS parent. There is no cross-parent data leak — `getMenuState`
 *   narrows on that parent's assignments — but an expired link would still open their own task,
 *   and on a shared device a second parent would silently act under the first one's identity.
 * - Without the scope check, `clear` breaks the ordinary case. The token is stripped from the URL
 *   on capture, so a plain page RELOAD arrives with no `?token=` and would wipe the credential the
 *   parent is mid-way through using. Storage exists precisely to survive that reload; clearing on
 *   every tokenless load would defeat the thing it is for.
 *
 * ⛔ **The scope is the whole address — path AND query — not the path alone.** Every task page is
 * `/workflow-tasks`, so a path-only comparison treats task B's tokenless link as a reload of task
 * A's and hands it A's identity. That is the same defect as having no check at all, for the case
 * it matters most in. `token` is excluded from the scope because it is removed from the URL on
 * capture, so including it would make the reload never match. Params are sorted so a reordered
 * query string is still recognised as the same address.
 *
 * ⚠️ Re-opening the SAME task at the SAME address through an expired link in the SAME tab still
 * works — that is indistinguishable from a reload. Narrowing further would need a task-scoped
 * credential; the JWT is parent-scoped, so there is nothing here to check it against.
 */
export const decideTokenAction = (
  urlToken: string | null,
  currentScope: string,
  storedScope: string | null
): TokenAction => {
  if (urlToken !== null && urlToken !== '') {
    return { type: 'store', token: urlToken, scope: currentScope };
  }
  if (storedScope !== null && storedScope !== currentScope) {
    return { type: 'clear' };
  }
  return { type: 'keep' };
};

/**
 * The address a stored token belongs to: pathname plus every query param except `token`, sorted.
 *
 * Built from a URL that may still carry the token, so it is dropped here rather than depending on
 * the caller having stripped it first — the stored scope must equal the scope computed on the next
 * load, and that load has no token.
 */
export const tokenScopeOf = (url: URL): string => {
  const params = new URLSearchParams(url.searchParams);
  params.delete(TOKEN_QUERY_PARAM);
  params.sort();
  const query = params.toString();
  return query === '' ? url.pathname : `${url.pathname}?${query}`;
};

type StoredToken = { token: string; scope: string };

/**
 * What this page load decided, independent of whether `sessionStorage` accepted it.
 *
 * ⚠️ `undefined` means "this load has not written anything", which is NOT the same as `null`
 * ("this load cleared it"). The difference is what stops a clear that failed to reach storage from
 * being undone by the stale record still sitting there.
 */
let inMemoryToken: StoredToken | null | undefined = undefined;

/**
 * ⛔ **Memory wins whenever this page load has written it; storage is only for surviving a
 * reload.** `writeStored` sets memory unconditionally and storage best-effort, so within one load
 * memory is always at least as fresh — and `sessionStorage` fails in exactly the way that makes
 * that matter. Safari private mode and some webviews let `getItem` SUCCEED while `setItem` throws,
 * so the write is silently lost and the store keeps whatever it had:
 *
 * - Empty store → returning null would drop the token just captured, send the first request
 *   unauthenticated, and tell the parent their perfectly good link expired.
 * - **Stale store → returning the old record is worse: the request goes out under the PREVIOUS
 *   parent's credential**, and their task resolves as missing rather than as the one in the link.
 *
 * Both are the same root cause, so both take the same answer rather than a special case each.
 *
 * Reading storage only when memory is untouched also keeps the reload path intact — that is the
 * one case where storage genuinely knows something memory does not.
 */
const readStored = (): StoredToken | null => {
  if (inMemoryToken !== undefined) return inMemoryToken;

  let raw: string | null;
  try {
    raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredToken).token === 'string' &&
      typeof (parsed as StoredToken).scope === 'string'
    ) {
      return parsed as StoredToken;
    }
  } catch {
    // Unparseable — treat as absent rather than trusting half a record.
  }
  return null;
};

/**
 * ⛔ **A failed write must also INVALIDATE what is still stored.** Memory covers the rest of this
 * page load, but memory does not survive a reload — and the record the failed write was meant to
 * replace does. Leaving it there means a reload resurrects a credential this load already
 * superseded, and the next request goes out as the PREVIOUS parent.
 *
 * Dropping it instead makes that reload unauthenticated, which the page reports as an expired
 * link. That is the right trade: a parent re-opening their link is recoverable, authenticating as
 * somebody else is not.
 *
 * If the `removeItem` fails too there is nothing further to try — the store is wholly unusable, so
 * `getItem` will not be returning anything from it either.
 */
const writeStored = (value: StoredToken | null): void => {
  inMemoryToken = value;
  try {
    if (value === null) {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      // Storage is unusable in both directions; memory is already set and is all there is.
    }
  }
};

/**
 * Takes the token out of the URL and keeps it for the rest of the tab's session.
 *
 * ⛔ **The token is stripped from the address bar on purpose.** It is a bearer credential with a
 * 30-day life (`TINY_URL_EXPIRY_MS`) and it authorizes the parent for every `petParentProcedure`,
 * not just this page — so leaving it in the URL puts it in browser history, in the `Referer` of any
 * outbound link, and in whatever the parent pastes when they share the page. `replaceState` also
 * means Back does not walk into a URL that re-seeds it.
 *
 * `sessionStorage` rather than `localStorage`: per-tab and cleared when the tab closes, which
 * matches how long the parent is actually answering the question for.
 */
export const capturePetParentToken = (): void => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const action = decideTokenAction(
    url.searchParams.get(TOKEN_QUERY_PARAM),
    tokenScopeOf(url),
    readStored()?.scope ?? null
  );

  switch (action.type) {
    case 'store':
      writeStored({ token: action.token, scope: action.scope });
      url.searchParams.delete(TOKEN_QUERY_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
      return;
    case 'clear':
      writeStored(null);
      return;
    case 'keep':
      return;
  }
};

/** The token for the current tab, or an empty string when the link carried none. */
export const getPetParentToken = (): string => readStored()?.token ?? '';
